/**
 * whisper-nonspeech-filter — drop Whisper's non-speech annotation segments.
 *
 * Whisper models (both whisper.cpp server-side and transformers.js in the
 * browser) were trained on YouTube captions, so on music / noise / silence
 * they emit caption-style annotations instead of transcript text:
 *
 *   "[музыка]" "[Music]" "[аплодисменты]" "(шумы)" "♪ ♪ ♪"
 *   "ТРЕВОЖНАЯ МУЗЫКА" (all-caps cue), "Редактор субтитров А.Семкин"
 *   (subtitle-credit hallucination on trailing silence/music)
 *
 * A paying user who uploads a music-heavy file gets a wall of
 * "[музыка] [музыка] [музыка]…" and reads it as a scam (comment #894,
 * 2026-07-01). Decoder-level suppression was measured on the production
 * whisper.cpp binary and REJECTED:
 *   --suppress-nst      → swaps tags for WORSE free-text hallucinations
 *                         ("ТРЕВОЖНАЯ МУЗЫКА", fake subtitle credits)
 *   --suppress-regex    → breaks decoding entirely ("no no no no…" loops)
 * The baseline decoder's failure mode is the most machine-detectable one,
 * so the эталон is: keep the decoder as-is, filter the annotations at
 * ingestion, and tell the user honestly when a file had no speech.
 *
 * Whole-segment matching only — a segment that MIXES real speech with a
 * tag is kept verbatim (measured: tags arrive as standalone segments).
 *
 * Shared across tools: speech-to-text/audio-to-text consume it today;
 * auto-captions / video-subtitles / interview-studio can adopt later.
 */
(function (global) {
    'use strict';

    /* Segment consists ONLY of bracketed/parenthesized groups + whitespace:
       "[музыка]", "(шумы)", "[музыка] [музыка]", "[Music] (applause)". */
    var ONLY_TAGS_RE = /^(\s*[\[(【][^\][()【】]*[\])】])+\s*$/;

    /* Music-note glyphs only: "♪", "♪♪ ♫", "♬ ♩". */
    var ONLY_NOTES_RE = /^[\s♪♫♬♩·.]+$/;

    /* All-caps caption cue: entire segment is uppercase (no lowercase
       letters anywhere) AND contains a known non-speech caption noun.
       Real speech transcribed by Whisper is mixed-case, so requiring
       every letter uppercase keeps false positives out ("громкая музыка
       играла" is kept; "ТРЕВОЖНАЯ МУЗЫКА" is dropped). */
    var CAPS_CUE_WORD_RE = /(МУЗЫКА|ЗВУКИ|ЗВУК|ШУМЫ|ШУМ|АПЛОДИСМЕНТЫ|СМЕХ|ТИШИНА|MUSIC|APPLAUSE|LAUGHTER|SILENCE)/;

    /* Subtitle-credit hallucinations — Whisper's RU training data ends
       captions with editor credits, and the model replays them on
       trailing silence/music (measured live on the prod binary). Match
       only at segment start, only these exact formulas. */
    var CREDITS_RE = /^(редактор\s+субтитров|корректор\s+[а-яё]|субтитры\s+(сделал|подготовил|создал|делал)|продолжение\s+следует)/i;

    function hasLowercase(s) {
        return /[a-zа-яё]/.test(s);
    }

    /**
     * True when a transcript segment is a non-speech annotation the user
     * should never see as "their transcription".
     */
    function isAnnotation(text) {
        var t = (text == null ? '' : String(text)).trim();
        if (!t) return false;
        if (ONLY_TAGS_RE.test(t)) return true;
        if (ONLY_NOTES_RE.test(t)) return true;
        if (CREDITS_RE.test(t)) return true;
        if (!hasLowercase(t) && CAPS_CUE_WORD_RE.test(t)) return true;
        return false;
    }

    /**
     * Filter an array of segment-like objects. `getText` extracts the text
     * from an item (defaults to item.text). Returns kept items + counts so
     * callers can surface an honest "no speech found" notice.
     */
    function filter(items, getText) {
        items = items || [];
        getText = getText || function (it) { return it && it.text; };
        var kept = [], dropped = 0;
        for (var i = 0; i < items.length; i++) {
            if (isAnnotation(getText(items[i]))) dropped++;
            else kept.push(items[i]);
        }
        return { kept: kept, dropped: dropped, total: items.length };
    }

    var api = { isAnnotation: isAnnotation, filter: filter };

    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    global.WhisperNonSpeech = api;
})(typeof window !== 'undefined' ? window : globalThis);
